import React, { useState, useEffect, useCallback } from 'react';
import industryDataset from './data/IndustryDataset';
import "../styles/steps.css";
import { FaLaptopCode, FaHome, FaTools } from "react-icons/fa";

const PreferredResources = ({ formData, updateFormData, setStepValidation, stepNumber }) => {
    const [error, setError] = useState('');
    const [newRole, setNewRole] = useState('');
    const [newRate, setNewRate] = useState('');
    const [newCount, setNewCount] = useState(1);


    // Track default roles separately
    const [defaultRoles, setDefaultRoles] = useState([]);

    // Load default roles when industry changes
    useEffect(() => {
        if (formData.industry && industryDataset[formData.industry]) {
            setDefaultRoles(industryDataset[formData.industry]?.preferredResources || []);
        }
    }, [formData.industry]);



    // Validation Function
    const validateFields = useCallback((nextClicked = false) => {
        let errorMessage = '';
        //console.log("Debugging - preferredResources:", formData.preferredResources);

        if (nextClicked) {
            if (formData.preferredResources.length === 0) {
                errorMessage = 'Please select at least one role or add a custom role.';
            } else {
                const invalidRoles = formData.preferredResources.some(role =>
                    role.hourlyRate <= 0 || role.count < 1
                );

                if (invalidRoles) {
                    errorMessage = 'All selected roles must have a valid hourly rate and count.';
                }
            }
        }

        setError(errorMessage);
        return errorMessage === ''; // Return true if valid
    }, [formData.preferredResources]);

    useEffect(() => {
        if (setStepValidation) {
            setStepValidation(stepNumber, validateFields);
        }
    }, [setStepValidation, stepNumber, validateFields]);

    // Handle Role Selection
    const handleRoleChange = (roleName) => {

        const isSelected = formData.preferredResources.some(role => role.name === roleName);
        let updatedResources;

        if (!isSelected) {
            const defaultRole = defaultRoles.find(r => r.name === roleName);
            updatedResources = [
                ...formData.preferredResources,
                {
                    name: roleName,
                    hourlyRate: defaultRole?.hourlyRate || 50,
                    count: defaultRole?.count || 1
                }
            ];
        } else {
            updatedResources = formData.preferredResources.filter(role => role.name !== roleName);
        }

        updateFormData({ preferredResources: updatedResources });
        updateFormData({
            allocationMatrix: [],
            resourceTable: [],
            resourceAllocationGrid: []
        });
    };

    // Handle Rate Change
    const handleRateChange = (roleName, rate) => {

        const updatedResources = formData.preferredResources.map(role =>
            role.name === roleName ? { ...role, hourlyRate: parseInt(rate) || 0 } : role
        );
        updateFormData({ preferredResources: updatedResources });
        updateFormData({
            allocationMatrix: [],
            resourceTable: [],
            resourceAllocationGrid: []
        });
    };

    // Handle Count Change
    const handleCountChange = (roleName, count) => {

        const updatedResources = formData.preferredResources.map(role =>
            role.name === roleName ? { ...role, count: parseInt(count) || 1 } : role
        );
        updateFormData({ preferredResources: updatedResources });
        updateFormData({
            allocationMatrix: [],
            resourceTable: [],
            resourceAllocationGrid: []
        });
    };

    // Handle Adding a Custom Role
    const handleAddCustomRole = () => {
        if (!newRole.trim()) {
            alert("Please enter a valid role name.");
            return;
        }

        if (formData.preferredResources.some(role => role.name === newRole)) {
            alert("This role already exists.");
            return;
        }

        const newRoleObject = { name: newRole, hourlyRate: parseInt(newRate) || 50, count: parseInt(newCount) || 1 };

        updateFormData({ preferredResources: [...formData.preferredResources, newRoleObject] });

        setNewRole('');
        setNewRate('');
        setNewCount(1);

        updateFormData({
            allocationMatrix: [],
            resourceTable: [],
            resourceAllocationGrid: []
        });
    };

    // Generate allRoles list (Combining Default & Custom Roles)
    const allRoles = [
        // Default roles with user-modified values if selected
        ...defaultRoles.map(role => {
            const selectedRole = formData.preferredResources.find(r => r.name === role.name);
            return selectedRole ? selectedRole : role; // Use modified role if selected, else default
        }),

        // Custom roles (not part of defaultRoles)
        ...formData.preferredResources.filter(role => !defaultRoles.some(defaultRole => defaultRole.name === role.name))
    ];

    return (
        <div className="step-container">
            <div className="step-description">
                <p>
                    As a Project Sponsor, select the resources your project requires and set their hourly rates. You can also add new resources based on your industry needs. If adding a similar resource, use a numeric identifier (e.g., Engineer 2) for clarity. Resource needs vary by industry and project complexity—select what’s essential. Soft Estimate will fine-tune your selection and allow adjustments later.</p>

                {/* Suggested Resources by Industry */}
                <p>Suggested resources based on your selected industry:</p>
                <ul>
                    <li><FaLaptopCode className="icon" /> Software Development: Developers, Project Managers, UX/UI Designers, QA Engineers, DevOps</li>
                    <li><FaHome className="icon" /> House Remodeling: Electricians, Plumbers, Interior Designers, Site Supervisors</li>
                    <li><FaTools className="icon" /> General Contracting: Construction Managers, Laborers, Engineers, Inspectors</li>
                </ul>

                <p> Tip: The accuracy of your estimate depends on the resources you choose—pick wisely!</p>
            </div>

            <br></br>
            {/* Display Default and Custom Roles */}
            <div className="role-list">
                {allRoles.map((role, index) => (
                    <div key={index} className="role-item">
                        <label className="role-label">
                            <input
                                type="checkbox"
                                checked={(formData.preferredResources.length > 0) && formData.preferredResources.some(r => r.name === role.name)}
                                onChange={() => handleRoleChange(role.name)}
                                className="role-checkbox"
                            />
                            <span>{role.name}</span>
                        </label>
                        {formData.preferredResources.some(r => r.name === role.name) && (
                            <div className="rate-count-group">
                                <input
                                    type="number"
                                    value={role.hourlyRate}
                                    onChange={(e) => handleRateChange(role.name, e.target.value)}
                                    placeholder="Hourly Rate ($)"
                                    className="rate-field"
                                />
                                <span className="rate-unit">$/hr</span>
                                <input
                                    type="number"
                                    value={role.count}
                                    onChange={(e) => handleCountChange(role.name, e.target.value)}
                                    placeholder="Count"
                                    className="count-field"
                                    min="1"
                                />
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Add Custom Role Section */}
            <div className="add-custom-role">
                <h3 className="section-title">Add New Resource:</h3>
                <div className="add-new-resource">
                    <input
                        type="text"
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value)}
                        placeholder="Resource Profession"
                        className="custom-role-input"
                    />
                    <input
                        type="number"
                        value={newRate}
                        onChange={(e) => setNewRate(e.target.value)}
                        placeholder="Hourly Rate($)"
                        className="custom-rate-input"
                    />
                    <input
                        type="number"
                        value={newCount}
                        onChange={(e) => setNewCount(e.target.value)}
                        placeholder="Count"
                        className="custom-count-input"
                        min="1"
                    />
                    <button onClick={handleAddCustomRole} className="add-button">
                        Add New Resource
                    </button>
                </div>
            </div>

            {/* Show error message inside the step if validation fails */}
            {error && <p style={{ color: 'red' }}>{error}</p>}
        </div>
    );
};

export default PreferredResources;